import { __experimentalToolsPanel as ToolsPanel } from '@wordpress/components';
import { useDispatch, useSelect } from '@wordpress/data';
import { useCallback } from '@wordpress/element';
import { store as blockEditorStore } from '../../store';
import { cleanEmptyObject } from '../../hooks/utils';
import { useToolsPanelDropdownMenuProps } from '../global-styles/utils';
import type {
	BlockSupportResetAllFilter,
	BlockSupportToolsPanelProps,
} from './types';

export default function BlockSupportToolsPanel( {
	children,
	group,
	label,
}: BlockSupportToolsPanelProps ) {
	const { updateBlockAttributes } = useDispatch( blockEditorStore );
	const {
		getBlockAttributes,
		getBlockName,
		getMultiSelectedBlockClientIds,
		getSelectedBlockClientId,
		hasMultiSelection,
	} = useSelect( blockEditorStore );
	const dropdownMenuProps = useToolsPanelDropdownMenuProps();
	const panelId = getSelectedBlockClientId();
	const resetAll = useCallback(
		( resetFilters: BlockSupportResetAllFilter[] = [] ) => {
			const newAttributes: Record<
				string,
				Record< string, unknown >
			> = {};

			// The panel only renders while a block is selected.
			const clientIds = hasMultiSelection()
				? getMultiSelectedBlockClientIds()
				: [ panelId! ];

			clientIds.forEach( ( clientId ) => {
				const blockAttributes = getBlockAttributes( clientId ) || {};
				const { style } = blockAttributes;
				let newBlockAttributes: Record< string, unknown > = { style };
				const resetContext = {
					attributes: blockAttributes,
					clientId,
					name: getBlockName( clientId ),
				};

				resetFilters.forEach( ( resetFilter ) => {
					newBlockAttributes = {
						...newBlockAttributes,
						...resetFilter( newBlockAttributes, resetContext ),
					};
				} );

				// Enforce a cleaned style object.
				newBlockAttributes = {
					...newBlockAttributes,
					style: cleanEmptyObject( newBlockAttributes.style ),
				};

				newAttributes[ clientId ] = newBlockAttributes;
			} );

			updateBlockAttributes( clientIds, newAttributes, true );
		},
		[
			getBlockAttributes,
			getBlockName,
			getMultiSelectedBlockClientIds,
			hasMultiSelection,
			panelId,
			updateBlockAttributes,
		]
	);

	return (
		<ToolsPanel
			className={ `${ group }-block-support-panel` }
			label={ label }
			resetAll={ resetAll }
			key={ panelId }
			panelId={ panelId }
			hasInnerWrapper
			shouldRenderPlaceholderItems // Required to maintain fills ordering.
			__experimentalFirstVisibleItemClass="first"
			__experimentalLastVisibleItemClass="last"
			dropdownMenuProps={ dropdownMenuProps }
		>
			{ children }
		</ToolsPanel>
	);
}
